import React from 'react';
import {
  C,
  PRESET_TEXT_W,
  PRESET_TEXT_CARD_H,
  PRESET_TEXT_CARD_H_NO_HEADER,
  PRESET_TEXT_CARD_GAP,
  CIRCLE_R,
  PLUS_R,
  BRANCH_STEM,
} from '../flowCanvas.constants';

const PAD_X     = 12;
const FIELD_H   = 26;
const DIVIDER_Y = 32;  // horizontal rule under the title
const NAME_Y    = 46;  // label top
const NAME_IN_Y = 52;  // input top
const HEADER_Y  = 90;  // label top
const HEAD_IN_Y = 96;  // input top
const BODY_Y    = 134; // label top (with header)
const BODY_IN_Y = 140; // input top (with header)
const BODY_Y_NH = 90;  // label top (no header)
const BODY_IN_Y_NH = 96; // input top (no header)
const TEXTAREA_H = 34;

const inputStyle = {
  width: '100%',
  height: '100%',
  background: 'var(--fc-circle-bg)',
  border: '1px solid var(--fc-border)',
  borderRadius: '5px',
  outline: 'none',
  color: 'var(--fc-text)',
  fontSize: '11px',
  fontFamily: 'inherit',
  padding: '4px 8px',
  boxSizing: 'border-box',
};

const textAreaStyle = {
  ...inputStyle,
  resize: 'none',
  paddingTop: '6px',
  lineHeight: '1.3',
};

function stopBubble(e) {
  e.stopPropagation();
}

export default function PresetTextNode({
  step,
  stepX,
  stepCY,
  parentStep,
  parentStepX,
  parentStepY,
  nextStep,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  nextMergeX,
  items,
  headersEnabled = true,
  activeStepId,
  activePresetIndex,
  onPresetClick,
  onPresetFieldChange,
  onRemovePreset,
}) {
  const presets = Array.isArray(items) ? items.filter(Boolean) : [];
  const hasItems = presets.length > 0;
  const rows = hasItems ? presets : [null];
  const count = rows.length;
  const cardH = headersEnabled ? PRESET_TEXT_CARD_H : PRESET_TEXT_CARD_H_NO_HEADER;

  const parentRadius = parentStep?.nodeConfig?.nodeStyle === 'infoButton' || parentStep?.nodeConfig?.nodeStyle === 'addButton'
    ? PLUS_R
    : CIRCLE_R;
  const hasParent = parentStepX !== null && parentStepX !== undefined;
  const hasNext = nextStep && nextStepX !== null && nextStepX !== undefined && nextStepY !== null && nextStepY !== undefined;

  const totalH = count * (cardH + PRESET_TEXT_CARD_GAP) - PRESET_TEXT_CARD_GAP;
  const cardCentreX = stepX;
  const cardLeftX = cardCentreX - PRESET_TEXT_W / 2;
  const stackTopY = stepCY - totalH / 2;

  const sourceX = hasParent ? parentStepX + parentRadius : cardLeftX - BRANCH_STEM;
  const sourceY = parentStepY ?? stepCY;
  const branchMidX = Math.max(sourceX + BRANCH_STEM, (sourceX + cardLeftX) / 2);
  const firstCardY = stackTopY + cardH / 2;
  const lastCardY = stackTopY + (count - 1) * (cardH + PRESET_TEXT_CARD_GAP) + cardH / 2;

  const rightEdgeX = cardCentreX + PRESET_TEXT_W / 2;
  const exitSpineX = rightEdgeX + 34;
  const mergeSpineX = hasNext
    ? (nextMergeX ?? Math.min(rightEdgeX + 42, nextStepX - (nextStepHalfW || 0) - 40))
    : rightEdgeX + 42;
  const nextAnchorX = hasNext ? nextStepX - (nextStepHalfW || 0) : null;
  const joinsSharedMerge = hasNext && nextMergeX != null;
  const branchColor = hasItems ? C.purple : C.orange;

  return (
    <>
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          <line
            x1={sourceX}
            y1={sourceY}
            x2={branchMidX}
            y2={sourceY}
            stroke={branchColor}
            strokeWidth="1.5"
            strokeOpacity="0.78"
            strokeLinecap="square"
          />
          {Math.abs(sourceY - stepCY) > 2 && (
            <line
              x1={branchMidX}
              y1={sourceY}
              x2={branchMidX}
              y2={stepCY}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.78"
              strokeLinecap="square"
            />
          )}
          {(count > 1 || Math.abs(stepCY - firstCardY) > 2 || Math.abs(stepCY - lastCardY) > 2) && (
            <line
              x1={branchMidX}
              y1={Math.min(firstCardY, stepCY)}
              x2={branchMidX}
              y2={Math.max(lastCardY, stepCY)}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.5"
            />
          )}
          {rows.map((_, index) => {
            const cy = stackTopY + index * (cardH + PRESET_TEXT_CARD_GAP) + cardH / 2;
            return (
              <React.Fragment key={`preset-branch-${index}`}>
                <line
                  x1={branchMidX}
                  y1={cy}
                  x2={cardLeftX}
                  y2={cy}
                  stroke={branchColor}
                  strokeWidth="1.5"
                  strokeOpacity="0.78"
                  strokeLinecap="square"
                />
                <circle cx={cardLeftX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
        </g>
      )}

      {rows.map((preset, index) => {
        const cy = stackTopY + index * (cardH + PRESET_TEXT_CARD_GAP) + cardH / 2;
        const top = cy - cardH / 2;
        const isSelected = activeStepId === step.stepId && activePresetIndex === index;
        const bodyLabelY  = headersEnabled ? BODY_Y    : BODY_Y_NH;
        const bodyInputY  = headersEnabled ? BODY_IN_Y : BODY_IN_Y_NH;
        const bodyInputH  = TEXTAREA_H;
        const trashCX = cardLeftX + PRESET_TEXT_W - 18;
        const trashCY = top + 18;

        return (
          <g
            key={`${step.stepId}-${index}`}
            className="fc-node fc-node--preset-text-card"
            onClick={() => onPresetClick?.(index)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={cardLeftX + 2}
              y={top + 4}
              width={PRESET_TEXT_W}
              height={cardH}
              rx="8"
              fill="#000"
              fillOpacity="0.3"
              style={{ pointerEvents: 'none' }}
            />
            <rect
              x={cardLeftX}
              y={top}
              width={PRESET_TEXT_W}
              height={cardH}
              rx="8"
              fill={isSelected ? C.cardSel : C.card}
              stroke={isSelected ? C.sel : branchColor}
              strokeWidth={isSelected ? 2 : 1.5}
            />
            <text
              x={cardLeftX + PAD_X}
              y={top + 22}
              fontSize="12"
              fontWeight="700"
              fill={C.text}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {preset?.presetName?.trim?.() || `Preset ${index + 1}`}
            </text>
            {preset && onRemovePreset && (
              <g
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePreset(index);
                }}
                style={{ cursor: 'pointer' }}
              >
                <circle cx={trashCX} cy={trashCY} r={9} fill="#dc2626" />
                <g
                  transform={`translate(${trashCX}, ${trashCY})`}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  <rect x="-2" y="-5" width="4" height="2" rx="1" fill="#fee2e2" />
                  <rect x="-4.5" y="-3" width="9" height="1.5" rx="0.75" fill="#fee2e2" />
                  <rect x="-3.5" y="-1" width="7" height="5.5" rx="0.8" fill="#fee2e2" />
                  <rect x="-1.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                  <rect x="0.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                </g>
              </g>
            )}
            <line
              x1={cardLeftX + 1} y1={top + DIVIDER_Y}
              x2={cardLeftX + PRESET_TEXT_W - 1} y2={top + DIVIDER_Y}
              stroke={C.border} strokeWidth="1"
              style={{ pointerEvents: 'none' }}
            />

            <text x={cardLeftX + PAD_X} y={top + NAME_Y} fontSize="10" fontWeight="600" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              Preset Name
            </text>
            <foreignObject x={cardLeftX + PAD_X} y={top + NAME_IN_Y} width={PRESET_TEXT_W - PAD_X * 2} height={FIELD_H}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%' }}>
                <input
                  value={preset?.presetName ?? ''}
                  placeholder={`Preset ${index + 1}`}
                  onClick={stopBubble}
                  onMouseDown={stopBubble}
                  onChange={(e) => onPresetFieldChange?.(index, 'presetName', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </foreignObject>

            {headersEnabled && (
              <>
                <text x={cardLeftX + PAD_X} y={top + HEADER_Y} fontSize="10" fontWeight="600" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  Header Text
                </text>
                <foreignObject x={cardLeftX + PAD_X} y={top + HEAD_IN_Y} width={PRESET_TEXT_W - PAD_X * 2} height={FIELD_H}>
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%' }}>
                    <input
                      value={preset?.headerText ?? ''}
                      placeholder="Header text…"
                      onClick={stopBubble}
                      onMouseDown={stopBubble}
                      onChange={(e) => onPresetFieldChange?.(index, 'headerText', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </foreignObject>
              </>
            )}

            <text x={cardLeftX + PAD_X} y={top + bodyLabelY} fontSize="10" fontWeight="600" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              Body Copy
            </text>
            <foreignObject x={cardLeftX + PAD_X} y={top + bodyInputY} width={PRESET_TEXT_W - PAD_X * 2} height={bodyInputH}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%' }}>
                <textarea
                  value={preset?.mainContent ?? ''}
                  placeholder="Body text…"
                  onClick={stopBubble}
                  onMouseDown={stopBubble}
                  onChange={(e) => onPresetFieldChange?.(index, 'mainContent', e.target.value)}
                  style={textAreaStyle}
                />
              </div>
            </foreignObject>

            {!preset && (
              <text
                x={cardCentreX}
                y={top + cardH - 16}
                textAnchor="middle"
                fontSize="9"
                fill={C.textDim}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                Use the add button to create your first preset
              </text>
            )}
          </g>
        );
      })}

      {hasNext && (
        <g style={{ pointerEvents: 'none' }}>
          {rows.map((_, index) => {
            const cy = stackTopY + index * (cardH + PRESET_TEXT_CARD_GAP) + cardH / 2;
            return (
              <React.Fragment key={`preset-exit-${index}`}>
                <line
                  x1={rightEdgeX}
                  y1={cy}
                  x2={exitSpineX}
                  y2={cy}
                  stroke={branchColor}
                  strokeWidth="1.5"
                  strokeOpacity="0.78"
                  strokeLinecap="square"
                />
                <circle cx={rightEdgeX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          {(count > 1 || Math.abs(stepCY - nextStepY) > 2) && (
            <line
              x1={exitSpineX}
              y1={Math.min(firstCardY, nextStepY)}
              x2={exitSpineX}
              y2={Math.max(lastCardY, nextStepY)}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.5"
            />
          )}
          <path
            d={joinsSharedMerge
              ? `M ${exitSpineX} ${nextStepY} H ${mergeSpineX}`
              : `M ${exitSpineX} ${nextStepY} H ${mergeSpineX} H ${nextAnchorX}`}
            stroke={branchColor}
            strokeWidth="1.5"
            fill="none"
            strokeOpacity="0.78"
            strokeLinecap="square"
          />
          {!joinsSharedMerge && (
            <circle cx={nextAnchorX} cy={nextStepY} r="3" fill={branchColor} fillOpacity="0.9" />
          )}
        </g>
      )}
    </>
  );
}
